import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { RefreshCw, Save, RotateCcw, Send, Eye, Mail } from "lucide-react";

/** Minimal, safe formatting keys for the body editor. We deliberately
 *  restrict what an admin can insert — no <script>, no arbitrary tags. */
const TAG_ACTIONS = [
  { label: "Bold",   wrap: ["<strong>", "</strong>"] },
  { label: "Italic", wrap: ["<em>", "</em>"] },
  { label: "Link",   prompt: "URL:", template: (url) => [`<a href="${url}">`, "</a>"] },
  { label: "Paragraph", block: "<p>{}</p>" },
  { label: "Bullet list", block: "<ul>\n  <li>{}</li>\n</ul>" },
];

const wrapSelection = (ta, before, after) => {
  const s = ta.selectionStart, e = ta.selectionEnd;
  const val = ta.value;
  const sel = val.slice(s, e);
  const next = val.slice(0, s) + before + sel + after + val.slice(e);
  return { value: next, cursor: s + before.length + sel.length + after.length };
};

const VariablesChip = ({ vars, onInsert }) => (
  <div className="flex flex-wrap gap-1.5">
    {vars.map(v => (
      <button
        key={v}
        type="button"
        onClick={() => onInsert(`{{${v}}}`)}
        className="text-[11px] px-2 py-1 rounded"
        style={{ background: "rgba(205,163,78,0.12)", color: "var(--gold-hover)", border: "1px solid rgba(205,163,78,0.35)" }}
        data-testid={`var-chip-${v}`}
      >
        {`{{${v}}}`}
      </button>
    ))}
  </div>
);

const CMSEmailTemplates = () => {
  const [list, setList] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/email-templates");
      setList(data || []);
      if (!selectedKey && data?.[0]) setSelectedKey(data[0].key);
    } catch (e) { toast({ title: "Load failed", description: "Could not fetch templates." }); }
  }, [selectedKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedKey) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/email-templates/${selectedKey}`);
        setTpl(data); setDirty(false); setPreviewHtml(null);
      } catch (e) { toast({ title: "Load failed" }); }
    })();
  }, [selectedKey]);

  const upd = (field, val) => { setTpl(t => ({ ...t, current: { ...t.current, [field]: val } })); setDirty(true); };

  const bodyRef = React.useRef(null);
  const insertAtBody = (frag) => {
    const ta = bodyRef.current; if (!ta) return;
    const { value, cursor } = wrapSelection(ta, frag, "");
    upd("body_html", value);
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = cursor; }, 0);
  };
  const applyFormat = (action) => {
    const ta = bodyRef.current; if (!ta) return;
    let before, after;
    if (action.wrap) { [before, after] = action.wrap; }
    else if (action.prompt) {
      const url = window.prompt(action.prompt); if (!url) return;
      [before, after] = action.template(url);
    }
    else if (action.block) {
      const sel = ta.value.slice(ta.selectionStart, ta.selectionEnd) || "text here";
      const block = action.block.replace("{}", sel);
      const next = ta.value.slice(0, ta.selectionStart) + block + ta.value.slice(ta.selectionEnd);
      upd("body_html", next);
      return;
    }
    const { value, cursor } = wrapSelection(ta, before, after);
    upd("body_html", value);
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = cursor; }, 0);
  };

  const save = async () => {
    if (!tpl) return;
    setBusy(true);
    try {
      const { data } = await adminApi.patch(`/admin/email-templates/${tpl.key}`, {
        subject: tpl.current.subject, preheader: tpl.current.preheader,
        body_html: tpl.current.body_html, cta_text: tpl.current.cta_text,
      });
      setTpl(data); setDirty(false);
      toast({ title: "Saved", description: `${data.name} updated.` });
      load();
    } catch (e) { toast({ title: "Save failed" }); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    if (!tpl) return;
    if (!window.confirm(`Restore ${tpl.name} to the original Vemoye template? Your custom edits for this email will be replaced.`)) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/email-templates/${tpl.key}/restore`);
      setTpl(data); setDirty(false);
      toast({ title: "Restored", description: `${data.name} reset to default.` });
      load();
    } catch (e) { toast({ title: "Restore failed" }); }
    finally { setBusy(false); }
  };

  const preview = async () => {
    if (dirty) await save();
    try {
      const { data } = await adminApi.get(`/admin/email-templates/${tpl.key}/preview`);
      setPreviewHtml({ subject: data.subject, html: data.html });
    } catch (e) { toast({ title: "Preview failed" }); }
  };

  const testSend = async () => {
    if (!testTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo)) { toast({ title: "Enter a valid test email" }); return; }
    if (dirty) await save();
    setBusy(true);
    try {
      await adminApi.post(`/admin/email-templates/${tpl.key}/test-send`, { to: testTo });
      toast({ title: "Test sent", description: `Marked [TEST] and sent to ${testTo}.` });
    } catch (e) { toast({ title: "Test send failed" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="cms-email-templates">
      <aside className="rounded-lg p-3" style={{ background: "var(--cream)", border: "1px solid rgba(18,52,40,0.1)" }}>
        <div className="section-eyebrow px-2 mb-2" style={{ color: "rgba(18,52,40,0.6)" }}>Templates ({list.length})</div>
        <div className="flex flex-col gap-1">
          {list.map(t => (
            <button
              key={t.key}
              onClick={() => setSelectedKey(t.key)}
              className="text-left px-3 py-2 rounded-md text-[13px]"
              data-testid={`tpl-item-${t.key}`}
              style={{
                background: selectedKey === t.key ? "rgba(205,163,78,0.18)" : "transparent",
                color: selectedKey === t.key ? "var(--forest)" : "rgba(18,52,40,0.85)",
                border: `1px solid ${selectedKey === t.key ? "var(--gold)" : "transparent"}`,
              }}
            >
              <div className="font-medium truncate">{t.name}</div>
              <div className="text-[11px] flex items-center gap-1" style={{ color: "rgba(18,52,40,0.55)" }}>
                {t.is_customised && <span style={{ color: "var(--gold-hover)" }}>● Custom</span>}
                {!t.is_customised && <span>Default</span>}
                {t.has_secure_link && <span>· Secure</span>}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {!tpl && <div className="p-8 text-[14px]" style={{ color: "rgba(18,52,40,0.6)" }}>Select a template to edit.</div>}
      {tpl && (
        <section className="rounded-lg p-6" style={{ background: "var(--cream)", border: "1px solid rgba(18,52,40,0.1)" }}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="section-eyebrow mb-1" style={{ color: "rgba(18,52,40,0.6)" }}>Template · {tpl.key}</div>
              <h2 className="classic-title text-[24px]" style={{ color: "var(--forest)" }}>{tpl.name}</h2>
              <p className="text-[13px] mt-1 max-w-[600px]" style={{ color: "rgba(18,52,40,0.7)" }}>{tpl.description}</p>
              {tpl.last_edited_at && (
                <p className="text-[11px] mt-2" style={{ color: "rgba(18,52,40,0.5)" }}>
                  Last edited by <strong>{tpl.last_edited_by}</strong> · {new Date(tpl.last_edited_at).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={preview} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-outline text-[12px]" data-testid="tpl-preview-btn"><Eye size={12} /> Preview</button>
              <button onClick={save} disabled={busy || !dirty} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-gold text-[12px] disabled:opacity-50" data-testid="tpl-save-btn"><Save size={12} /> Save</button>
              <button onClick={restore} disabled={busy || !tpl.is_customised} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-ghost text-[12px] disabled:opacity-40" data-testid="tpl-restore-btn"><RotateCcw size={12} /> Restore</button>
            </div>
          </div>

          <div className="mb-3">
            <label className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(18,52,40,0.55)" }}>Subject</label>
            <input value={tpl.current.subject} onChange={e => upd("subject", e.target.value)} data-testid="tpl-subject" className="w-full mt-1 px-3 py-2 rounded-md bg-white border text-[14px]" style={{ borderColor: "rgba(18,52,40,0.15)" }} />
          </div>
          <div className="mb-3">
            <label className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(18,52,40,0.55)" }}>Preheader / preview text</label>
            <input value={tpl.current.preheader} onChange={e => upd("preheader", e.target.value)} data-testid="tpl-preheader" className="w-full mt-1 px-3 py-2 rounded-md bg-white border text-[14px]" style={{ borderColor: "rgba(18,52,40,0.15)" }} />
          </div>

          <div className="mb-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(18,52,40,0.55)" }}>Body</label>
              <div className="flex flex-wrap gap-1">
                {TAG_ACTIONS.map(a => (
                  <button key={a.label} type="button" onClick={() => applyFormat(a)} className="text-[11px] px-2 py-1 rounded bg-white border" style={{ borderColor: "rgba(18,52,40,0.15)", color: "var(--forest)" }} data-testid={`tpl-fmt-${a.label.toLowerCase().replace(/\s+/g,'-')}`}>{a.label}</button>
                ))}
              </div>
            </div>
            <textarea ref={bodyRef} rows={10} value={tpl.current.body_html} onChange={e => upd("body_html", e.target.value)} data-testid="tpl-body" className="w-full mt-1 px-3 py-2 rounded-md bg-white border text-[13px] font-mono" style={{ borderColor: "rgba(18,52,40,0.15)", lineHeight: 1.5 }} />
            <p className="text-[11px] mt-1" style={{ color: "rgba(18,52,40,0.55)" }}>Use the formatting buttons to safely insert bold, italic, links and lists. Vemoye's branded header, logo, footer and layout are always applied around your content.</p>
          </div>

          {tpl.cta_url_var && (
            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider" style={{ color: "rgba(18,52,40,0.55)" }}>CTA button text</label>
              <input value={tpl.current.cta_text} onChange={e => upd("cta_text", e.target.value)} data-testid="tpl-cta-text" className="w-full mt-1 px-3 py-2 rounded-md bg-white border text-[14px]" style={{ borderColor: "rgba(18,52,40,0.15)" }} />
              <p className="text-[11px] mt-1" style={{ color: "rgba(18,52,40,0.55)" }}>
                The button destination is <code>{`{{${tpl.cta_url_var}}}`}</code> and is generated by the system for security. {tpl.has_secure_link && <><strong>This is a secure link</strong> — the button will always appear even if you remove it from the body.</>}
              </p>
            </div>
          )}

          <div className="rounded-md p-3 mb-4" style={{ background: "rgba(205,163,78,0.06)", border: "1px solid rgba(205,163,78,0.35)" }}>
            <div className="section-eyebrow mb-2" style={{ color: "var(--gold-hover)" }}>Available personalisation fields</div>
            {tpl.allowed_vars?.length ? <VariablesChip vars={tpl.allowed_vars} onInsert={insertAtBody} /> : <p className="text-[12px]" style={{ color: "rgba(18,52,40,0.6)" }}>No dynamic variables for this template.</p>}
            <p className="text-[11px] mt-2" style={{ color: "rgba(18,52,40,0.55)" }}>Click a chip to insert it at the body cursor. Empty values render as blank.</p>
          </div>

          <div className="rounded-md p-3 flex flex-wrap items-center gap-3" style={{ background: "white", border: "1px solid rgba(18,52,40,0.1)" }}>
            <Mail size={14} style={{ color: "var(--forest)" }} />
            <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="test@address.com" data-testid="tpl-test-to" className="flex-1 min-w-[220px] px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: "rgba(18,52,40,0.15)" }} />
            <button onClick={testSend} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md btn-forest text-[12px]" data-testid="tpl-test-btn"><Send size={12} /> Send test email</button>
          </div>
        </section>
      )}

      {previewHtml && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(18,52,40,0.65)" }} onClick={() => setPreviewHtml(null)} data-testid="tpl-preview-modal">
          <div className="bg-white rounded-lg max-w-[720px] w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(18,52,40,0.1)" }}>
              <div className="text-[13px]" style={{ color: "rgba(18,52,40,0.7)" }}><strong>Subject:</strong> {previewHtml.subject}</div>
              <button onClick={() => setPreviewHtml(null)} className="text-[12px] px-2 py-1 rounded" style={{ color: "rgba(18,52,40,0.7)" }}>Close</button>
            </div>
            <iframe title="Email preview" srcDoc={previewHtml.html} className="w-full flex-1" style={{ border: "none", minHeight: "500px" }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSEmailTemplates;
